import { useMutation, useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import { apiFetch, ApiClientError } from "@/lib/api-client";
import { queryKeys } from "@/lib/query-keys";

// POST /api/portal/deals/:id/withdraw
//
// Phase 7 sub-bucket 5 — partner withdraws a submitted-but-pending deal.
//
// Pessimistic. Server soft-state: sets status='closed_lost' AND prepends a
// "[WITHDRAWN]" marker to notes (deal_status enum lacks a 'withdrawn'
// value; enum changes are out of sub-bucket scope). Display layer detects
// the marker to render "Withdrawn" instead of "Closed - lost".
//
// 404 → "deal_not_found"; 409 → "deal_not_editable" (e.g. status already
// transitioned). Caller drives the confirmation modal (the hook doesn't
// own UI flow).
export function useWithdrawDeal(dealId: string) {
  const queryClient = useQueryClient();
  return useMutation<{ ok: true }, ApiClientError, void>({
    mutationFn: () =>
      apiFetch<{ ok: true }>(`/portal/deals/${dealId}/withdraw`, {
        method: "POST",
      }),
    onSuccess: () => {
      void queryClient.invalidateQueries({
        queryKey: queryKeys.portal.deals.list(),
      });
      void queryClient.invalidateQueries({
        queryKey: queryKeys.portal.deals.detail(dealId),
      });
      void queryClient.invalidateQueries({
        queryKey: queryKeys.portal.deals.timeline(dealId),
      });
      void queryClient.invalidateQueries({
        queryKey: queryKeys.portal.dealSummary(),
      });
    },
    onError: (err) => {
      toast.error(err.message ?? "Failed to withdraw deal");
    },
  });
}
